import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Header from '../../../Components/Header';
import Sidebar from '../../../Components/Sidebar';
import { Link } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { 
  collection, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  orderBy 
} from 'firebase/firestore';
import { db } from '../../../config/firebase';

const ToDosBackground = () => {
  
  // ToDo Board State
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState('idea');
  const [editingTicket, setEditingTicket] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketFormData, setTicketFormData] = useState({
    title: '',
    description: '',
    status: 'idea',
    blocked: false,
    implemented: false,
    priority: 'medium',
    assignee: '',
    dueDate: '',
    storyPoints: ''
  });

  const columns = [
    { id: 'idea', title: 'Idea', color: '#e3f2fd' },
    { id: 'todo', title: 'ToDo', color: '#fff3e0' },
    { id: 'planned', title: 'Planned', color: '#f3e5f5' },
    { id: 'inprogress', title: 'In Progress', color: '#e8f5e8' },
    { id: 'done', title: 'Done', color: '#f0f4c3' }
  ];

  useEffect(() => {
    // Set up real-time listener for tickets
    const ticketsRef = collection(db, 'todoTickets');
    const q = query(ticketsRef, orderBy('order', 'asc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const ticketData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTickets(ticketData);
      setTicketsLoading(false);
    }, (error) => {
      console.error('Error fetching tickets:', error);
      toast.error('Error loading tickets');
      setTicketsLoading(false);
    });

    return () => unsubscribe();
  }, []);


  // ToDo Board Functions
  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    try {
      const newStatus = destination.droppableId;
      
      // Check if moving to "planned" status and validate story points requirement
      if (newStatus === 'planned') {
        const ticket = tickets.find(t => t.id === draggableId);
        if (!ticket || !ticket.storyPoints || ticket.storyPoints === '') {
          toast.error('Story Points are required before moving ticket to Planned status');
          return;
        }
      }
      
      await updateDoc(doc(db, 'todoTickets', draggableId), {
        status: newStatus,
        order: destination.index,
        updatedAt: new Date()
      });

      toast.success('Ticket moved successfully');
    } catch (error) {
      console.error('Error moving ticket:', error);
      toast.error('Error moving ticket');
    }
  };

  const handleAddTicket = () => {
    setEditingTicket(null);
    setTicketFormData({
      title: '',
      description: '',
      status: selectedColumn,
      blocked: false,
      implemented: false,
      priority: 'medium',
      assignee: '',
      dueDate: '',
      storyPoints: ''
    });
    setShowAddModal(true);
  };

  const handleEditTicket = (ticket) => {
    setEditingTicket(ticket);
    setTicketFormData({
      title: ticket.title || '',
      description: ticket.description || '',
      status: ticket.status || 'idea',
      blocked: ticket.blocked || false,
      implemented: ticket.implemented || false,
      priority: ticket.priority || 'medium',
      assignee: ticket.assignee || '',
      dueDate: ticket.dueDate || '',
      storyPoints: ticket.storyPoints || ''
    });
    setShowAddModal(true);
  };

  const handleTicketSubmit = async (e) => {
    e.preventDefault();
    
    if (!ticketFormData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    try {
      const ticketData = {
        ...ticketFormData,
        updatedAt: new Date(),
        order: tickets.filter(t => t.status === ticketFormData.status).length
      };

      if (editingTicket) {
        await updateDoc(doc(db, 'todoTickets', editingTicket.id), ticketData);
        toast.success('Ticket updated successfully');
      } else {
        ticketData.createdAt = new Date();
        await addDoc(collection(db, 'todoTickets'), ticketData);
        toast.success('Ticket created successfully');
      }

      setShowAddModal(false);
      setEditingTicket(null);
    } catch (error) {
      console.error('Error saving ticket:', error);
      toast.error('Error saving ticket');
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    if (window.confirm('Are you sure you want to delete this ticket?')) {
      try {
        await deleteDoc(doc(db, 'todoTickets', ticketId));
        toast.success('Ticket deleted successfully');
      } catch (error) {
        console.error('Error deleting ticket:', error);
        toast.error('Error deleting ticket');
      }
    }
  };

  const handleStatusToggle = async (ticketId, field, currentValue) => {
    try {
      await updateDoc(doc(db, 'todoTickets', ticketId), {
        [field]: !currentValue,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      toast.error(`Error updating ${field}`);
    }
  };

  const getTicketsByColumn = (columnId) => {
    return tickets.filter(ticket => ticket.status === columnId);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#ff5722';
      case 'medium': return '#ff9800';
      case 'low': return '#4caf50';
      default: return '#757575';
    }
  };

  const getPlannedStoriesStats = () => {
    const plannedTickets = tickets.filter(ticket => ticket.status === 'planned');
    const totalStories = plannedTickets.length;
    const totalCost = plannedTickets.reduce((sum, ticket) => {
      const storyPoints = parseInt(ticket.storyPoints) || 0;
      return sum + (storyPoints * 100);
    }, 0);
    
    return { totalStories, totalCost };
  };

  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
    setShowTicketModal(true);
  };

  return (
    <>
      <Header />
      <Sidebar
        id="content-menu"
        id1="content-items"
        activeClassName="todos-background"
      />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/herocarousel">Website Content</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right">
                      <FeatherIcon icon="chevron-right" />
                    </i>
                  </li>
                  <li className="breadcrumb-item active">ToDo Board</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-body">
                  {/* ToDo Board */}
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                          <h3 className="mb-2">ToDo Board</h3>
                          {/* Planned Stories Statistics */}
                          <div className="d-flex gap-3">
                            <div className="bg-light rounded px-3 py-2">
                              <small className="text-muted d-block">Total Planned Stories</small>
                              <strong className="text-primary">{getPlannedStoriesStats().totalStories}</strong>
                            </div>
                            <div className="bg-light rounded px-3 py-2">
                              <small className="text-muted d-block">Total Planned Cost</small>
                              <strong className="text-success">€{getPlannedStoriesStats().totalCost.toLocaleString()}</strong>
                            </div>
                          </div>
                        </div>
                        <button 
                          className="btn btn-primary"
                          onClick={() => {
                            setSelectedColumn('idea');
                            handleAddTicket();
                          }}
                        >
                          <FeatherIcon icon="plus" size={16} className="me-2" />
                          Add Ticket
                        </button>
                      </div>

                      {ticketsLoading ? (
                        <div className="d-flex justify-content-center py-5">
                          <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      ) : (
                        <DragDropContext onDragEnd={handleDragEnd}>
                          <div className="row">
                            {columns.map(column => (
                              <div key={column.id} className="col mb-3">
                                <div 
                                  className="card h-100"
                                  style={{ backgroundColor: column.color, minHeight: '500px' }}
                                >
                                  <div className="card-header bg-transparent border-0">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <h6 className="mb-0 fw-bold">{column.title}</h6>
                                      <span className="badge bg-secondary">
                                        {getTicketsByColumn(column.id).length}
                                      </span>
                                      <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => {
                                          setSelectedColumn(column.id);
                                          handleAddTicket();
                                        }}
                                      >
                                        <FeatherIcon icon="plus" size={14} />
                                      </button>
                                    </div>
                                  </div>
                                  
                                  <Droppable droppableId={column.id}>
                                    {(provided, snapshot) => (
                                      <div
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        className="card-body pt-2"
                                        style={{
                                          backgroundColor: snapshot.isDraggingOver ? '#f0f0f0' : 'transparent',
                                          minHeight: '400px'
                                        }}
                                      >
                                        {getTicketsByColumn(column.id).map((ticket, index) => (
                                          <Draggable key={ticket.id} draggableId={ticket.id} index={index}>
                                            {(provided, snapshot) => (
                                              <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                className="card mb-2 shadow-sm"
                                                style={{
                                                  ...provided.draggableProps.style,
                                                  backgroundColor: snapshot.isDragging ? '#fff' : '#fafafa',
                                                  border: snapshot.isDragging ? '2px solid #007bff' : '1px solid #ddd'
                                                }}
                                              >
                                                <div 
                                                  className="card-body p-2"
                                                  onClick={() => handleTicketClick(ticket)}
                                                  style={{ cursor: 'pointer' }}
                                                >
                                                  <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <h6 className="card-title mb-0 small text-truncate" style={{ maxWidth: '120px' }}>
                                                      {ticket.title}
                                                    </h6>
                                                    <div className="dropdown" onClick={(e) => e.stopPropagation()}>
                                                      <button
                                                        className="btn btn-sm btn-outline-secondary dropdown-toggle"
                                                        type="button"
                                                        data-bs-toggle="dropdown"
                                                        style={{ padding: '2px 6px' }}
                                                      >
                                                        <FeatherIcon icon="more-vertical" size={10} />
                                                      </button>
                                                      <ul className="dropdown-menu">
                                                        <li>
                                                          <button
                                                            className="dropdown-item"
                                                            onClick={() => handleEditTicket(ticket)}
                                                          >
                                                            <FeatherIcon icon="edit" size={14} className="me-2" />
                                                            Edit
                                                          </button>
                                                        </li>
                                                        <li>
                                                          <button
                                                            className="dropdown-item text-danger"
                                                            onClick={() => handleDeleteTicket(ticket.id)}
                                                          >
                                                            <FeatherIcon icon="trash-2" size={14} className="me-2" />
                                                            Delete
                                                          </button>
                                                        </li>
                                                      </ul>
                                                    </div>
                                                  </div>
                                                  
                                                  <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <div className="d-flex gap-1">
                                                      <span
                                                        className="badge badge-sm"
                                                        style={{ 
                                                          backgroundColor: getPriorityColor(ticket.priority),
                                                          fontSize: '0.7rem',
                                                          padding: '2px 6px'
                                                        }}
                                                      >
                                                        {ticket.priority}
                                                      </span>
                                                      {ticket.storyPoints && (
                                                        <span
                                                          className="badge bg-primary badge-sm"
                                                          style={{ 
                                                            fontSize: '0.7rem',
                                                            padding: '2px 6px'
                                                          }}
                                                          title={`${ticket.storyPoints} story points (€${ticket.storyPoints * 100})`}
                                                        >
                                                          {ticket.storyPoints}SP
                                                        </span>
                                                      )}
                                                    </div>
                                                    {ticket.assignee && (
                                                      <small className="text-muted text-truncate" style={{ maxWidth: '60px' }}>
                                                        {ticket.assignee}
                                                      </small>
                                                    )}
                                                  </div>

                                                  <div className="d-flex justify-content-center gap-2 mt-2">
                                                    {ticket.blocked && (
                                                      <span className="badge bg-danger" style={{ fontSize: '0.6rem' }}>
                                                        Blocked
                                                      </span>
                                                    )}
                                                    {ticket.implemented && (
                                                      <span className="badge bg-success" style={{ fontSize: '0.6rem' }}>
                                                        Done
                                                      </span>
                                                    )}
                                                  </div>

                                                  {ticket.dueDate && (
                                                    <small className="text-muted d-block text-center mt-1" style={{ fontSize: '0.7rem' }}>
                                                      {new Date(ticket.dueDate).toLocaleDateString()}
                                                    </small>
                                                  )}
                                                </div>
                                              </div>
                                            )}
                                          </Draggable>
                                        ))}
                                        {provided.placeholder}
                                      </div>
                                    )}
                                  </Droppable>
                                </div>
                              </div>
                            ))}
                          </div>
                        </DragDropContext>
                      )}
                    </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Add/Edit Ticket Modal */}
      {showAddModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingTicket ? 'Edit Ticket' : 'Add New Ticket'}
                </h5>
                <button 
                  type="button" 
                  className="btn-close"
                  onClick={() => setShowAddModal(false)}
                ></button>
              </div>
              <form onSubmit={handleTicketSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Title *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={ticketFormData.title}
                      onChange={(e) => setTicketFormData({ ...ticketFormData, title: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={ticketFormData.description}
                      onChange={(e) => setTicketFormData({ ...ticketFormData, description: e.target.value })}
                    />
                  </div>
                  
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Status</label>
                      <select
                        className="form-control"
                        value={ticketFormData.status}
                        onChange={(e) => setTicketFormData({ ...ticketFormData, status: e.target.value })}
                      >
                        {columns.map(column => (
                          <option key={column.id} value={column.id}>{column.title}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Priority</label>
                      <select
                        className="form-control"
                        value={ticketFormData.priority}
                        onChange={(e) => setTicketFormData({ ...ticketFormData, priority: e.target.value })}
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Assignee</label>
                      <input
                        type="text"
                        className="form-control"
                        value={ticketFormData.assignee}
                        onChange={(e) => setTicketFormData({ ...ticketFormData, assignee: e.target.value })}
                      />
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Due Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={ticketFormData.dueDate}
                        onChange={(e) => setTicketFormData({ ...ticketFormData, dueDate: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Story Points <small className="text-muted">(1 point = €100)</small></label>
                      <select
                        className="form-control"
                        value={ticketFormData.storyPoints}
                        onChange={(e) => setTicketFormData({ ...ticketFormData, storyPoints: e.target.value })}
                      >
                        <option value="">Select story points</option>
                        <option value="1">1 Point (€100)</option>
                        <option value="2">2 Points (€200)</option>
                        <option value="3">3 Points (€300)</option>
                        <option value="4">4 Points (€400)</option>
                        <option value="5">5 Points (€500)</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={ticketFormData.blocked}
                          onChange={(e) => setTicketFormData({ ...ticketFormData, blocked: e.target.checked })}
                        />
                        <label className="form-check-label">Blocked</label>
                      </div>
                    </div>
                    
                    <div className="col-md-6">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={ticketFormData.implemented}
                          onChange={(e) => setTicketFormData({ ...ticketFormData, implemented: e.target.checked })}
                        />
                        <label className="form-check-label">Implemented</label>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => setShowAddModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingTicket ? 'Update' : 'Create'} Ticket
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Ticket Details Modal */}
      {showTicketModal && selectedTicket && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title d-flex align-items-center">
                  <span
                    className="badge me-2"
                    style={{ backgroundColor: getPriorityColor(selectedTicket.priority) }}
                  >
                    {selectedTicket.priority}
                  </span>
                  {selectedTicket.title}
                </h5>
                <button 
                  type="button" 
                  className="btn-close"
                  onClick={() => setShowTicketModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-8">
                    <div className="mb-3">
                      <label className="form-label fw-bold">Description</label>
                      <div className="p-3 border rounded bg-light">
                        {selectedTicket.description ? (
                          <p className="mb-0">{selectedTicket.description}</p>
                        ) : (
                          <p className="text-muted mb-0 fst-italic">No description provided</p>
                        )}
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-4 mb-3">
                        <label className="form-label fw-bold">Status</label>
                        <div className="p-2 border rounded bg-light">
                          <span className="badge" style={{ backgroundColor: columns.find(c => c.id === selectedTicket.status)?.color || '#e9ecef' }}>
                            {columns.find(c => c.id === selectedTicket.status)?.title || selectedTicket.status}
                          </span>
                        </div>
                      </div>
                      
                      <div className="col-md-4 mb-3">
                        <label className="form-label fw-bold">Priority</label>
                        <div className="p-2 border rounded bg-light">
                          <span
                            className="badge"
                            style={{ backgroundColor: getPriorityColor(selectedTicket.priority) }}
                          >
                            {selectedTicket.priority?.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      
                      <div className="col-md-4 mb-3">
                        <label className="form-label fw-bold">Story Points</label>
                        <div className="p-2 border rounded bg-light">
                          {selectedTicket.storyPoints ? (
                            <span className="badge bg-primary">
                              {selectedTicket.storyPoints} SP (€{selectedTicket.storyPoints * 100})
                            </span>
                          ) : (
                            <span className="text-muted fst-italic">Not set</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold">Assignee</label>
                        <div className="p-2 border rounded bg-light">
                          {selectedTicket.assignee || <span className="text-muted fst-italic">Not assigned</span>}
                        </div>
                      </div>
                      
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold">Due Date</label>
                        <div className="p-2 border rounded bg-light">
                          {selectedTicket.dueDate ? (
                            new Date(selectedTicket.dueDate).toLocaleDateString('en-US', {
                              weekday: 'short',
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })
                          ) : (
                            <span className="text-muted fst-italic">No due date</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label fw-bold">Internal Status</label>
                      <div className="d-flex flex-column gap-2">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={selectedTicket.blocked || false}
                            onChange={() => {
                              handleStatusToggle(selectedTicket.id, 'blocked', selectedTicket.blocked);
                              setSelectedTicket({...selectedTicket, blocked: !selectedTicket.blocked});
                            }}
                          />
                          <label className="form-check-label">
                            Blocked
                            {selectedTicket.blocked && <span className="badge bg-danger ms-2">Yes</span>}
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={selectedTicket.implemented || false}
                            onChange={() => {
                              handleStatusToggle(selectedTicket.id, 'implemented', selectedTicket.implemented);
                              setSelectedTicket({...selectedTicket, implemented: !selectedTicket.implemented});
                            }}
                          />
                          <label className="form-check-label">
                            Implemented
                            {selectedTicket.implemented && <span className="badge bg-success ms-2">Yes</span>}
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-bold">Timestamps</label>
                      <div className="small text-muted">
                        {selectedTicket.createdAt && (
                          <div>
                            <strong>Created:</strong><br />
                            {new Date(selectedTicket.createdAt.seconds * 1000).toLocaleString()}
                          </div>
                        )}
                        {selectedTicket.updatedAt && (
                          <div className="mt-2">
                            <strong>Updated:</strong><br />
                            {new Date(selectedTicket.updatedAt.seconds * 1000).toLocaleString()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setShowTicketModal(false)}
                >
                  Close
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={() => {
                    setShowTicketModal(false);
                    handleEditTicket(selectedTicket);
                  }}
                >
                  <FeatherIcon icon="edit" size={16} className="me-2" />
                  Edit Ticket
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </>
  );
};

export default ToDosBackground;